import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

import { fg } from '@atlaskit/platform-feature-flags';

import { isSupportedByLinkingPlatform, type RegexStorage } from '../../linking-platform/utils';

const linkPatternStart = '\\bhttps?:\\/\\/(?:www\\.)?';
const linkPatternEnd = '[^\\s\\]\\)]*';

// If we want to re-introduce canva support, please use the regex from this
// PR which was made redundant: https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/135407/commits/0fcf626f035396cfcb87153a4d8d4721c844b07a#platform%2Fpackages%2Fai-mate%2Fconversation-assistant%2Fsrc%2Fcommon%2Futils%2Fcontent-transformer%2Fmd-tokenizer%2Fmd-plugin-smartlink%2Findex.ts
// Right now the support is dropped in favour of supporting what the Canva Agent needs.
// It's likely first-class Enhanced Smart links  (e.g `(my canva link "smartCard-embed")[http://some.canva.link]`
// will be how we support this in the future
const linkRegex = {
	youtube: `${linkPatternStart}youtube\\.com${linkPatternEnd}`,
	youtubeShortUrl: `${linkPatternStart}youtu\\.be${linkPatternEnd}`,
	loomVideo: `${linkPatternStart}loom\\.com\\/share\\/${linkPatternEnd}`,
	figmaFile: `${linkPatternStart}figma\\.com\\/(file|design)\\/${linkPatternEnd}`,
	confluenceWhiteboard: `${linkPatternStart}.*\\/wiki\\/spaces\\/.*\\/whiteboard\\/${linkPatternEnd}`,
	googlePresentationAndSpreadsheet: `${linkPatternStart}docs\\.google\\.com\\/(presentation|spreadsheets)\\/d\\/${linkPatternEnd}`,
	googleDoc: `${linkPatternStart}docs\\.google\\.com\\/(document)\\/d\\/${linkPatternEnd}`,
	googleForm: `${linkPatternStart}docs\\.google\\.com\\/forms\\/d\\/${linkPatternEnd}`,
	microsoftForm: `${linkPatternStart}forms\\.office\\.com\\/Pages\\/ResponsePage\\.aspx\\?id\\=${linkPatternEnd}`, // this will be removed following project
	analyticsChart: `\\bhttps://[^.]+\\.(atlassian\\.net|jira-dev\\.com)/analytics/[^/]+/chart-${linkPatternEnd}`, // this will be removed following project
};

const supportedEmbedProviders = Object.values(linkRegex).join('|');
const embedRegex = new RegExp(supportedEmbedProviders, 'g');

const embedInlineRegex = [
	// avpRovoSkillsChart
	`${linkPatternStart}avp-rovo-skills\\.[-\\w]+.frontend.public.atl-paas.net/v1/chart[^\\s\\)]+${linkPatternEnd}`,
];

const avpChartsRegex = `${linkPatternStart}.*?\\/avpviz\\/c\\/[^\\/]+.*${linkPatternEnd}`;

/**
 * Checks if there is more than one link in the token stream.
 * Used to determine if all links should be rendered inline instead of as embeds.
 */
const hasMultipleLinks = (tokens: Token[]): boolean => {
	let linkCount = 0;

	for (const token of tokens) {
		if (token.type !== 'inline' || !token.children) {
			continue;
		}

		for (const childToken of token.children) {
			if (childToken?.type === 'link_open') {
				linkCount++;
				if (linkCount > 1) {
					return true; // Early exit once we find more than one
				}
			}
		}
	}

	return false;
};

const createSmartLinks =
	({
		regexStorage,
		isNonAgentMessage,
		isExperimentEnabled,
	}: {
		regexStorage: RegexStorage;
		isNonAgentMessage?: boolean;
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean;
	}) =>
	(state: StateCore): void => {
		const tokens: Token[] = [];
		const embedTokens: Token[] = [];

		let embedMatches: string[] = [];
		let filteredTokens: Token[] = [];

		// process each token to determine if the link should be converted to a smartlink
		for (const token of state.tokens) {
			let processedTokenChildren: Array<Token> = [];

			const regexMatchesRaw = token.content?.trim().match(embedRegex);
			const regexMatches: string[] = regexMatchesRaw ? Array.from(new Set(regexMatchesRaw)) : [];
			embedMatches = [...embedMatches, ...regexMatches];

			// Any block or embed Smart Card links will be in an "inline" token. If
			// the current token is anything other than "inline", or there's no child tokens
			// to process, then we add it to the list of tokens to keep and move forward
			if (token.type !== 'inline' || !token.children) {
				tokens.push(token);
				continue;
			}

			var outputToken = new state.Token('inline', '', 0);
			let tokensToAppend: Token[] = [outputToken];

			if (token.children) {
				let discardUntilIndex = -1;

				token.children.forEach((childToken, i) => {
					const discardedToken =
						i <= discardUntilIndex && ['link_close', 'text'].includes(childToken.type);

					if (discardedToken) {
						return;
					}

					const url = childToken.attrGet('href') ?? '';
					const isInlineLink = url === token.children?.[i + 1]?.content;

					if (
						childToken?.type === 'link_open' &&
						(isSupportedByLinkingPlatform(url, regexStorage) || isInlineLink)
					) {
						// Discard the next two tokens. We're now processing the opening node for a URL
						// that will result in a new node that replaces it, therefore the subsequent link content and
						// closing node are no longer needed
						discardUntilIndex = i + 2;

						// Check if this link should be rendered as an embed card inline (e.g., Loom, YouTube, Figma) at end of result
						const matchesEmbedRegex = url.match(embedRegex);

						if (matchesEmbedRegex && !embedMatches.includes(url)) {
							embedMatches.push(url);
						}

						const shouldRenderAvpRovoSkillsEmbedInline =
							token.level <= 1 && embedInlineRegex.some((r) => url.match(r));

						const shouldRenderAvpChartsEmbedInline =
							token.level <= 1 && url.match(avpChartsRegex) && fg('ai_mate_unfurl_avp_charts');

						const shouldRenderAvpEmbedInline =
							shouldRenderAvpRovoSkillsEmbedInline || shouldRenderAvpChartsEmbedInline;

						if (shouldRenderAvpEmbedInline) {
							outputToken.children = processedTokenChildren;
							tokensToAppend.push(new state.Token('paragraph_close', '', 0));

							processedTokenChildren = [];

							let embedToken = new state.Token('embedCard', '', 0);
							embedToken.content = '';
							embedToken.block = true;
							embedToken.attrSet('url', url);
							embedToken.attrSet('layout', 'center');
							tokensToAppend.push(embedToken);

							tokensToAppend.push(new state.Token('paragraph_open', '', 0));

							outputToken = new state.Token('inline', '', 0);
							tokensToAppend.push(outputToken);

							return;
						}

						// create an inline card for the links that don't qualify
						let cardToken = new state.Token('inlineCard', '', 0);
						cardToken.content = '';
						cardToken.block = false;
						cardToken.level = childToken.level;
						cardToken.attrSet('url', url);
						processedTokenChildren.push(cardToken);

						return;
					}

					// pushes non link tokens as is
					processedTokenChildren.push(childToken);
				});
			}

			// set processed children to the output token
			outputToken.children = processedTokenChildren;

			// Keeps inline tokens that have children and all non-inline tokens.
			const blockTokensWithContent = tokensToAppend.filter(
				(t) => t.type !== 'inline' || t.children?.length,
			);
			tokens.push(...blockTokensWithContent);
		}

		// Remove empty paragraphs otherwise add it to the filteredTokens
		for (let i = 0; i < tokens.length; i++) {
			if (
				i < tokens.length - 1 &&
				tokens[i].type === 'paragraph_open' &&
				tokens[i + 1].type === 'paragraph_close'
			) {
				i = i + 1;
			} else {
				filteredTokens.push(tokens[i]);
			}
		}

		// Render all embeds at the end of the results if it is an agent message
		if (embedMatches?.length && !isNonAgentMessage && !isExperimentEnabled?.('rovo_chat_smartlink_embedcard_improvements', true)) {
			// Default behavior: render all embeds at the end
			[...new Set(embedMatches)].forEach((match) => {
				let embedToken = new state.Token('embedCard', '', 0);
				embedToken.content = '';
				embedToken.block = true;
				embedToken.attrSet('url', match);
				embedToken.attrSet('layout', 'center');
				embedTokens.push(embedToken);
			});
		}
		state.tokens = [...filteredTokens, ...embedTokens];

		return;
	};

const createSmartLinksRefresh =
	({
		regexStorage,
		isExperimentEnabled,
		shouldShowAsInline,
	}: {
		regexStorage: RegexStorage;
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean;
		shouldShowAsInline?: boolean;
	}) =>
	(state: StateCore): void => {
		const tokens: Token[] = [];
		const embedTokens: Token[] = [];

		let embedMatches: string[] = [];
		let filteredTokens: Token[] = [];
		const renderedInlineEmbeds = new Set<string>();

		// If there are more than one link or the user opened the chat in mini modal, all links display inline
		const forceAllToInline = hasMultipleLinks(state.tokens) || shouldShowAsInline;

		// process each token to determine if the link should be converted to a smartlink
		for (const token of state.tokens) {
			let processedTokenChildren: Array<Token> = [];

			const regexMatchesRaw = token.content?.trim().match(embedRegex);
			const regexMatches: string[] = regexMatchesRaw ? Array.from(new Set(regexMatchesRaw)) : [];
			embedMatches = [...embedMatches, ...regexMatches];

			// Any block or embed Smart Card links will be in an "inline" token. If
			// the current token is anything other than "inline", or there's no child tokens
			// to process, then we add it to the list of tokens to keep and move forward
			if (token.type !== 'inline' || !token.children) {
				tokens.push(token);
				continue;
			}

			var outputToken = new state.Token('inline', '', 0);
			let tokensToAppend: Token[] = [outputToken];

			if (token.children) {
				let discardUntilIndex = -1;

				token.children.forEach((childToken, i) => {
					const discardedToken =
						i <= discardUntilIndex && ['link_close', 'text'].includes(childToken.type);

					if (discardedToken) {
						return;
					}

					const url = childToken.attrGet('href') ?? '';
					const isInlineLink = url === token.children?.[i + 1]?.content;

					if (
						childToken?.type === 'link_open' &&
						(isSupportedByLinkingPlatform(url, regexStorage) || isInlineLink)
					) {
						// Discard the next two tokens. We're now processing the opening node for a URL
						// that will result in a new node that replaces it, therefore the subsequent link content and
						// closing node are no longer needed
						discardUntilIndex = i + 2;

						// Check if this link should be rendered as an embed card inline (e.g., Loom, YouTube, Figma) at end of result
						const matchesEmbedRegex = url.match(embedRegex);

						if (matchesEmbedRegex && !embedMatches.includes(url)) {
							embedMatches.push(url);
						}

						// handle condition for when link in the token content matches the linkRegex
						const shouldRenderLinksAsEmbed = matchesEmbedRegex;

						const shouldRenderAvpRovoSkillsEmbedInline =
							token.level <= 1 && embedInlineRegex.some((r) => url.match(r));

						const shouldRenderAvpChartsEmbedInline =
							token.level <= 1 && url.match(avpChartsRegex) && fg('ai_mate_unfurl_avp_charts');

						const shouldRenderAvpEmbedInline =
							shouldRenderAvpRovoSkillsEmbedInline || shouldRenderAvpChartsEmbedInline;

						// Render inline embed if either regex matches and token content is not part of a list
						const shouldRenderEmbedInline = shouldRenderLinksAsEmbed && token.level <= 1;

						if (
							shouldRenderAvpEmbedInline || (shouldRenderLinksAsEmbed &&
							isExperimentEnabled?.('rovo_chat_smartlink_embedcard_improvements', true))
						) {
							// handle case if it is an analytic chart link or microsoft form link
							// these should be rendered as inline cards, not embed cards
							const isMicrosoftFormLink = new RegExp(linkRegex.microsoftForm).test(url);
							const isAnalyticsChartLink = new RegExp(linkRegex.analyticsChart).test(url);

							if (
								(!forceAllToInline &&
								shouldRenderEmbedInline &&
								!isMicrosoftFormLink &&
								!isAnalyticsChartLink) || shouldRenderAvpEmbedInline
							) {
								outputToken.children = processedTokenChildren;
								tokensToAppend.push(new state.Token('paragraph_close', '', 0));

								processedTokenChildren = [];

								let embedToken = new state.Token('embedCard', '', 0);
								embedToken.content = '';
								embedToken.block = true;
								embedToken.attrSet('url', url);
								embedToken.attrSet('layout', 'center');
								tokensToAppend.push(embedToken);

								tokensToAppend.push(new state.Token('paragraph_open', '', 0));

								outputToken = new state.Token('inline', '', 0);
								tokensToAppend.push(outputToken);

								// Track that we've rendered this URL as an inline embed
								renderedInlineEmbeds.add(url);

								return;
							}
						}

						// create an inline card for the links that don't qualify
						let cardToken = new state.Token('inlineCard', '', 0);
						cardToken.content = '';
						cardToken.block = false;
						cardToken.level = childToken.level;
						cardToken.attrSet('url', url);
						processedTokenChildren.push(cardToken);

						return;
					}

					// pushes non link tokens as is
					processedTokenChildren.push(childToken);
				});
			}

			// set processed children to the output token
			outputToken.children = processedTokenChildren;

			// Keeps inline tokens that have children and all non-inline tokens.
			const blockTokensWithContent = tokensToAppend.filter(
				(t) => t.type !== 'inline' || t.children?.length,
			);
			tokens.push(...blockTokensWithContent);
		}

		// Remove empty paragraphs otherwise add it to the filteredTokens
		for (let i = 0; i < tokens.length; i++) {
			if (
				i < tokens.length - 1 &&
				tokens[i].type === 'paragraph_open' &&
				tokens[i + 1].type === 'paragraph_close'
			) {
				i = i + 1;
			} else {
				filteredTokens.push(tokens[i]);
			}
		}

		state.tokens = [...filteredTokens, ...embedTokens];

		return;
	};

export const createSmartLinksPlugin =
	(
		params: Parameters<typeof createSmartLinks>[0],
		isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
		shouldShowAsInline?: boolean,
		isNonAgentMessage?: boolean
	) =>
	(md: MarkdownIt): void =>
		md.core.ruler.push(
			'inlineCard',
			isExperimentEnabled?.('rovo_chat_smartlink_embedcard_improvements', false) ? createSmartLinksRefresh({ ...params, isExperimentEnabled, shouldShowAsInline }) :
			createSmartLinks({ ...params, isNonAgentMessage, isExperimentEnabled }),
		);
